import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "./authorization.functions";
import { projectDispatch, validDay, monday, type SavedRecord, type DispatchBoardData } from "./dispatch-board";
import { fetchOriginMap, filterRowsByOrigin } from "./order-origin.server";
import type { OrderRow } from "./orders.functions";

const input = z.object({ day: z.string().refine(validDay, "Choose a valid delivery date"), isDemo: z.boolean().default(false) });

export const getUnifiedDispatchBoard = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => input.parse(raw))
  .handler(async ({ context, data }): Promise<DispatchBoardData> => {
    await assertAdmin(context.supabase);
    const { data: tenant, error: tenantError } = await context.supabase.rpc("current_tenant_id");
    if (tenantError || !tenant) throw new Error("Dispatch access could not be verified.");
    const sb = context.supabase;
    // Only the order columns the board projection, UI and print views actually
    // read — the orders table has 70+ columns with large JSON snapshots, and
    // selecting them all for every historical undated order was the main
    // loading bottleneck.
    const ORDER_COLS = "id, tenant_id, order_ref, status, order_type, customer_name, phone, neighbourhood, pincode, address_line_1, address_line_2, delivery_address, meal_category, meal_plan_title, special_instructions, preferred_delivery_date, preferred_delivery_time, is_demo, payment_environment, payment_status, razorpay_payment_id, razorpay_order_id, cart_id, created_at";
    // Explicit tenant constraints supplement existing RLS; no privileged client or writes.
    const scoped = (table: "orders" | "order_items" | "order_lines" | "mp2_subscriptions" | "mp2_delivery_ledger") => {
      switch (table) {
        case "orders": return sb.from("orders").select(ORDER_COLS).eq("tenant_id", tenant);
        case "order_items": return sb.from("order_items").select("*").eq("tenant_id", tenant);
        case "order_lines": return sb.from("order_lines").select("*").eq("tenant_id", tenant);
        case "mp2_subscriptions": return sb.from("mp2_subscriptions").select("*").eq("tenant_id", tenant);
        case "mp2_delivery_ledger": return sb.from("mp2_delivery_ledger").select("*").eq("tenant_id", tenant);
      }
    };
    const all = async (query: ReturnType<typeof scoped>): Promise<SavedRecord[]> => {
      const rows: SavedRecord[] = [];
      for (let from = 0; ; from += 500) {
        const { data: page, error } = await query.range(from, from + 499);
        if (error) throw new Error("Dispatch deliveries could not be loaded. Please try again.");
        rows.push(...(page ?? []) as unknown as SavedRecord[]);
        if ((page?.length ?? 0) < 500) return rows;
      }
    };
    // Wave 1: every read that depends only on tenant + selected day runs in parallel.
    // Tiffin delivery dates live only on the saved subscription schedule (IST calendar days).
    const [ledger, scheduledItems, datedOrders, undatedOrders, tiffinResult, comboResult, menuResult] = await Promise.all([
      all(sb.from("mp2_delivery_ledger").select("*").eq("tenant_id", tenant).eq("delivery_date", data.day).order("id")),
      all(sb.from("order_items").select("*").eq("tenant_id", tenant).eq("delivery_date", data.day).order("id")),
      all(sb.from("orders").select(ORDER_COLS).eq("tenant_id", tenant).eq("preferred_delivery_date", data.day).order("id")),
      all(scoped("orders").is("preferred_delivery_date", null).not("status", "in", "(cancelled,failed,refunded,delivered)").order("id")),
      sb.from("tiffin_subscriptions")
        .select("id, order_id, status, duration_label, meal_selection, service_days, schedule_snapshot, address_snapshot, price_snapshot")
        .eq("tenant_id", tenant).contains("schedule_snapshot", `[${JSON.stringify(data.day)}]`).order("id").limit(1000),
      sb.from("mp2_meal_combinations").select("id, code").eq("tenant_id", tenant),
      sb.from("menu_cycles").select(`plan_id, food_preference_code, week_start_date, menu_cycle_days(day_of_week, menu_cycle_day_dishes(meal_slot, is_primary, mp2_dishes:dish_id(id, name, calories_kcal, protein_g)))`)
        .eq("tenant_id", tenant).eq("week_start_date", monday(data.day)).eq("pub_status", "published").eq("is_archived", false).order("created_at", { ascending: false }),
    ]);
    if (tiffinResult.error) throw new Error("Tiffin deliveries could not be loaded. Please try again.");
    if (comboResult.error || menuResult.error) throw new Error("Scheduled meal information could not be loaded. Please try again.");
    const tiffin = (tiffinResult.data ?? []) as unknown as SavedRecord[];
    const combinations = comboResult.data;
    const menus = menuResult.data;
    const chunkRead = async (table: Parameters<typeof scoped>[0], column: string, ids: string[]) => {
      const unique = [...new Set(ids.filter(Boolean))];
      const chunks: string[][] = [];
      for (let i = 0; i < unique.length; i += 100) chunks.push(unique.slice(i, i + 100));
      // Chunks are independent reads; run them in parallel instead of serially.
      const results = await Promise.all(chunks.map((c) => all(scoped(table).in(column, c).order("id"))));
      return results.flat();
    };
    const orderIds = [...scheduledItems.map(i => String(i.order_id)), ...datedOrders.map(o => String(o.id)), ...undatedOrders.map(o => String(o.id)), ...tiffin.map(t => String(t.order_id))];
    const [selectedSubs, orderSubs] = await Promise.all([
      chunkRead("mp2_subscriptions", "id", ledger.map(l => String(l.subscription_id))),
      chunkRead("mp2_subscriptions", "originating_order_id", orderIds),
    ]);
    const subscriptions = [...new Map([...selectedSubs, ...orderSubs].map(s => [String(s.id), s])).values()];
    orderIds.push(...subscriptions.map(s => String(s.originating_order_id)), ...ledger.map(l => l.fulfilled_order_id ? String(l.fulfilled_order_id) : ""));
    // Dated/undated orders are already loaded above; only fetch the extra ids
    // referenced by subscriptions or the ledger instead of re-reading every order.
    const wantedOrderIds = new Set(orderIds.filter(Boolean));
    const loadedOrders = new Map<string, SavedRecord>();
    for (const o of [...datedOrders, ...undatedOrders]) {
      const id = String(o.id);
      if (wantedOrderIds.has(id)) loadedOrders.set(id, o);
    }
    const missingOrderIds = [...wantedOrderIds].filter((id) => !loadedOrders.has(id));
    const extraOrders = await chunkRead("orders", "id", missingOrderIds);
    const fetchedOrders = [...loadedOrders.values(), ...extraOrders];
    // Dispatch Board follows the shared production/test classification: only
    // verified production orders are projected. Pre-migration (no
    // classification table) the existing provenance behaviour is unchanged.
    const originMap = await fetchOriginMap(sb, fetchedOrders.map(o => String(o.id)));
    const orders = originMap === null
      ? fetchedOrders
      : filterRowsByOrigin(fetchedOrders, originMap, "production");
    const [items, lines] = await Promise.all([
      chunkRead("order_items", "order_id", orderIds), chunkRead("order_lines", "order_id", orderIds),
    ]);
    const dishIds = [...new Set([...items, ...lines].map(i => i.dish_id ? String(i.dish_id) : "").filter(Boolean))];
    const dishes: SavedRecord[] = [], dispatch: SavedRecord[] = [];
    const dishChunks: string[][] = [];
    for (let i = 0; i < dishIds.length; i += 100) dishChunks.push(dishIds.slice(i, i + 100));
    await Promise.all(dishChunks.map(async (ids) => {
      const { data: found, error } = await sb.from("mp2_dishes").select("id, calories_kcal, protein_g").eq("tenant_id", tenant).in("id", ids);
      if (error) throw new Error("Nutrition information could not be loaded. Please try again.");
      dishes.push(...(found ?? []));
    }));
    const uniqueOrders = [...new Set(orderIds.filter(Boolean))];
    const audit: SavedRecord[] = [], payments: SavedRecord[] = [];
    const verificationOrderIds: string[] = [];
    const verificationHashes = ["\\x9a042ac2683d80f0f786d015fdbc23ef303d087b444da43b39c4f7f8e67c91b9", "\\x068e3d5861124ae90e2bfaa9e34e49dce483bd6160729a85d8f2392145ed85ba", "\\x4a536d8855535302ba5c8faff5b32fabc393240315783a6b3104ab30b671950c", "\\x2fda151e10786368623b3d1e81e91a2b44155bd128b9c39f520c1f72842b5bf5"];
    // Exact cart lineage from documented August 21 verification migrations, not identity heuristics.
    const { data: verificationCarts, error: cartError } = await sb.from("alacarte_carts").select("id")
      .eq("tenant_id", tenant).in("token_hash", verificationHashes);
    if (cartError) throw new Error("Delivery provenance could not be verified. Please try again.");
    const verificationCartIds = new Set((verificationCarts ?? []).map(c => c.id));
    for (const o of orders) if (verificationCartIds.has(textCartId(o.cart_id))) verificationOrderIds.push(String(o.id));
    const idChunks: string[][] = [];
    for (let i = 0; i < uniqueOrders.length; i += 100) idChunks.push(uniqueOrders.slice(i, i + 100));
    // Provenance and assignment reads are independent per chunk; run them in parallel.
    await Promise.all(idChunks.map(async (ids) => {
      const [auditResult, paymentResult, dispatchResult] = await Promise.all([
        sb.from("checkout_audit_log").select("order_id,event,outcome,checkout_mode,payment_environment,actor_is_admin").eq("tenant_id", tenant).in("order_id", ids),
        sb.from("order_payments").select("order_id,is_demo,status,signature_verified_at,captured_at,razorpay_order_id,razorpay_payment_id").eq("tenant_id", tenant).in("order_id", ids),
        sb.from("order_dispatch").select("*").eq("tenant_id", tenant).in("order_id", ids),
      ]);
      if (auditResult.error || paymentResult.error) throw new Error("Delivery provenance could not be verified. Please try again.");
      if (dispatchResult.error) throw new Error("Delivery assignments could not be loaded. Please try again.");
      audit.push(...(auditResult.data ?? [])); payments.push(...(paymentResult.data ?? []));
      dispatch.push(...(dispatchResult.data ?? []));
    }));
    return projectDispatch({ day: data.day, orders: orders as unknown as OrderRow[], items, lines, ledger, subscriptions, dishes, dispatch,
      combinations: combinations ?? [], menus: menus as unknown as SavedRecord[] ?? [], tiffin, provenance: { audit, payments, verificationOrderIds } });
  });

function textCartId(value: unknown) { return typeof value === "string" ? value : ""; }